import React, { useEffect, useState, useMemo, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import { LayoutGrid as AppsIcon, Bell as NotificationsIcon } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeContext';
import tokenManager from '../utils/tokenManager';
import axiosInstance from './api/axiosInstance';
import { ProfileCard, AppsMenuCard, useAccounts, uploadAvatar, UnifiedAvatar, buildServiceUrl } from '@aiinak/header';
import ThemeToggle from './ui/ThemeToggle';
import logger from '../utils/logger';
// TrialBanner removed — trial indicator shown in sidebar only

const Header = ({ showResumeIcons, onSearchChange }) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const { theme, resolvedTheme } = useTheme();
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const appMenuRef = useRef(null);
  const profileMenuRef = useRef(null);

  const {
    userData,
    userEmail,
    firstName,
    lastName,
    profilePicture,
    isAdmin,
    isBusinessOwner,
    hasAdminAccess
  } = useUser();

  const isDarkMode = resolvedTheme === 'dark';

  // Use the shared useAccounts hook for multi-account management
  const {
    accounts,
    currentAccount,
    setCurrentAccount,
    switchAccount,
    removeAccount,
    clearAccounts
  } = useAccounts({
    onAccountSwitch: async (account) => {
      if (account.token) {
        // Clear stale avatar immediately
        setAvatarUrl(null);

        // Set the token
        tokenManager.setToken(account.token);
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${account.token}`;

        // Create account without avatar to prevent stale images
        const accountWithoutAvatar = {
          ...account,
          avatarUrl: null,
          profilePicture: null
        };

        tokenManager.setAccountInfo(accountWithoutAvatar);

        // Clear avatar URLs from all stored accounts
        try {
          const storedAccounts = JSON.parse(localStorage.getItem('accounts') || '[]');
          const cleanedAccounts = storedAccounts.map(acc => ({
            ...acc,
            avatarUrl: null,
            profilePicture: null
          }));
          localStorage.setItem('accounts', JSON.stringify(cleanedAccounts));
        } catch (e) {
          logger.error('Error clearing cached avatars:', e);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
        window.location.reload();
      }
    }
  });

  // Check multiple admin fields for business owners - matching main app logic EXACTLY
  const isAdminUser = useMemo(() => {
    return hasAdminAccess ||
           userData?.isAdmin ||
           userData?.isBusinessOwner ||
           userData?.role === 'admin' ||
           userData?.role === 'super_admin';
  }, [hasAdminAccess, userData?.isAdmin, userData?.isBusinessOwner, userData?.role]);

  // Update current account from UserContext
  useEffect(() => {
    if (userData) {
      const accountData = {
        userId: userData.id,
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        avatarUrl: userData.profilePicture || userData.avatarUrl,
        profilePicture: userData.profilePicture || userData.avatarUrl,
        isAdmin: isAdmin || isBusinessOwner,
        accountType: userData.accountType,
        isBusinessOwner: isBusinessOwner,
        token: tokenManager.getToken(),
        loginTimestamp: Date.now()
      };

      setCurrentAccount(accountData);
      setAvatarUrl(userData.profilePicture || userData.avatarUrl);
    }
  }, [userData, isAdmin, isBusinessOwner, setCurrentAccount]);

  // Close menus on outside click
  useEffect(() => {
    if (!appMenuOpen && !profileMenuOpen) return;
    const handleClickOutside = (e) => {
      if (appMenuOpen && appMenuRef.current && !appMenuRef.current.contains(e.target)) {
        setAppMenuOpen(false);
      }
      if (profileMenuOpen && profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [appMenuOpen, profileMenuOpen]);

  const handleAvatarUpload = async (file) => {
    try {
      const userToken = tokenManager.getToken();
      if (!userToken) {
        notify('Authentication required', 'error');
        return;
      }

      notify('Uploading avatar...', 'info');

      const result = await uploadAvatar(file, userToken);

      if (result.avatarUrl) {
        setCurrentAccount(prev => ({
          ...prev,
          avatarUrl: result.avatarUrl,
          profilePicture: result.avatarUrl
        }));
        setAvatarUrl(result.avatarUrl);

        notify('Profile picture updated successfully', 'success');
      }
    } catch (error) {
      logger.error('Error uploading avatar:', error);
      notify('Failed to upload profile picture', 'error');
    }
  };

  const handleAppMenuOpen = () => {
    setProfileMenuOpen(false);
    setAppMenuOpen((v) => !v);
  };

  const handleAppMenuClose = () => {
    setAppMenuOpen(false);
  };

  const handleProfileMenuOpen = () => {
    setAppMenuOpen(false);
    setProfileMenuOpen((v) => !v);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {

      // First close menu to prevent visual glitches
      handleProfileMenuClose();

      // Show loading state
      setIsSwitchingAccount(true);

      // Use tokenManager comprehensive logout like main app
      await tokenManager.logout({
        redirect: true,
        redirectUrl: window.location.hostname.includes('localhost')
          ? '/login'
          : 'https://accounts.aiinak.com/login'
      });

      // Reset state
      clearAccounts();

    } catch (error) {
      logger.error('❌ Error during sign out:', error);

      // Fallback logout - comprehensive token removal

      // Clear ALL possible token names like main app
      const tokenNames = ['userToken', 'tempMfaToken', 'accountInfo', 'currentAccount', 'accounts'];
      tokenNames.forEach(tokenName => {
        tokenManager.removeToken(tokenName);
      });

      // Clear localStorage items
      localStorage.removeItem('currentAccount');
      localStorage.removeItem('accounts');
      localStorage.removeItem('darkMode');

      // Force redirect
      const redirectUrl = window.location.hostname.includes('localhost')
        ? '/login'
        : 'https://accounts.aiinak.com/login';

      window.location.replace(redirectUrl);
    } finally {
      setIsSwitchingAccount(false);
    }
  };

  const handleAddAccount = () => {
    const currentToken = tokenManager.getToken();
    const currentAccountData = localStorage.getItem('currentAccount');

    if (currentToken) {
      sessionStorage.setItem('previousUserToken', currentToken);

      if (currentAccountData) {
        sessionStorage.setItem('previousAccount', currentAccountData);
      } else {
        const userInfo = {
          firstName: firstName || '',
          lastName: lastName || '',
          email: userEmail || '',
          token: currentToken
        };
        sessionStorage.setItem('previousAccount', JSON.stringify(userInfo));
      }
    }

    handleProfileMenuClose();

    sessionStorage.setItem('addingNewAccount', 'true');

    tokenManager.removeToken('userToken');
    tokenManager.removeToken('accountInfo');

    localStorage.removeItem('currentAccount');

    if (window.location.hostname.includes('localhost')) {
      window.location.href = '/login?addAccount=true';
    } else {
      const timestamp = Date.now();
      window.location.href = `https://accounts.aiinak.com/login?addAccount=true&t=${timestamp}`;
    }
  };

  const handleSwitchAccount = async (account) => {
    try {
      handleProfileMenuClose();
      notify('Switching account...', 'info');

      // Use the switchAccount from useAccounts hook
      // The onAccountSwitch callback handles the actual switching logic
      await switchAccount(account);
    } catch (error) {
      logger.error('Error switching account:', error);
      notify('Failed to switch account', 'error');
    }
  };

  return (
    <>
      {/* Loading Overlay */}
      {isSwitchingAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 text-center border border-border shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-foreground">Switching account...</p>
          </div>
        </div>
      )}

      <header className="sticky top-0 w-full z-50 bg-background/95 backdrop-blur-sm shadow-sm border-b border-border">
        <div className="max-w-[1920px] mx-auto px-4 py-1.5 flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center">
            <div className="p-1.5 flex items-center justify-center">
              <img
                alt="Admin Logo"
                src='/menuicon/admin.webp'
                onClick={() => navigate("/")}
                className='cursor-pointer h-6 w-6 object-contain'
              />
            </div>
            <span className="ml-2 font-display font-medium text-foreground text-base tracking-tight">
              Admin Center
            </span>
          </div>

          {/* Right: Icons */}
          <div className="flex-1"></div>
          <div className="flex items-center space-x-1.5">
            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Apps Menu */}
            <div className="relative" ref={appMenuRef}>
              <button
                type="button"
                onClick={handleAppMenuOpen}
                className="p-1.5 hover:bg-accent rounded-full transition-colors duration-200"
              >
                <AppsIcon className="h-5 w-5 text-muted-foreground" />
              </button>

              {appMenuOpen && (
                <div className="absolute right-0 mt-2 z-50">
                  <AppsMenuCard
                    currentAccount={currentAccount}
                    accountType={userData?.accountType}
                    onNavigate={(path) => {
                      navigate(path);
                      handleAppMenuClose();
                    }}
                    onAppClick={(appName, url) => {
                      handleAppMenuClose();
                      window.open(url, '_blank');
                    }}
                    isDarkMode={isDarkMode}
                    forceShowAdmin={isAdminUser}
                  />
                </div>
              )}
            </div>

            {/* Notifications */}
            <button
              type="button"
              className="p-1.5 hover:bg-accent rounded-full transition-colors duration-200"
            >
              <NotificationsIcon className="h-5 w-5 text-muted-foreground" />
            </button>

            {/* Profile Menu */}
            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={handleProfileMenuOpen}
                className="p-0.5 hover:bg-accent rounded-full transition-colors duration-200"
              >
                <UnifiedAvatar
                  src={avatarUrl || currentAccount?.avatarUrl || currentAccount?.profilePicture}
                  firstName={currentAccount?.firstName || firstName}
                  lastName={currentAccount?.lastName || lastName}
                  email={currentAccount?.email || userEmail}
                  size={32}
                />
              </button>

              {profileMenuOpen && (
                <div
                  className="absolute right-0 mt-2 z-50 rounded-3xl border border-border bg-popover backdrop-blur-sm shadow-xl overflow-visible"
                  style={{ width: 340 }}
                >
                  <ProfileCard
                    currentAccount={currentAccount}
                    accounts={accounts}
                    avatarUrl={avatarUrl || currentAccount?.avatarUrl || currentAccount?.profilePicture}
                    manageAccountUrl={buildServiceUrl('accounts', '/profile')}
                    onAvatarUpload={handleAvatarUpload}
                    onSwitchAccount={handleSwitchAccount}
                    onAddAccount={handleAddAccount}
                    onSignOut={handleSignOut}
                    onSignOutAccount={(account) => {
                      removeAccount(account.email);
                      handleProfileMenuClose();
                    }}
                    isDarkMode={isDarkMode}
                    width={340}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <hr className="border-border" />
    </>
  );
};

export default memo(Header);
